import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import type { InfiniteSliderItemComponentProps } from '@hivebrite/design-system/InfiniteSlider';
import { Loader } from '@hivebrite/design-system/Loader';

import type { ComputedSliderItems } from 'hb-builder/components/BuilderComponents/block/Gallery/components/InfiniteSlider/components/index';
import {
  isLeftItem,
  isRightItem,
} from 'hb-builder/components/BuilderComponents/block/Gallery/components/InfiniteSlider/utils/getItemsPosition';
import type {
  StyledImageProps,
  StyledTextProps,
} from 'hb-builder/components/BuilderComponents/block/Gallery/types';
import LoadingBox from 'hb-builder/components/common/LoadingComponents/LoadingBox';
import { BACKGROUND_POSITION } from 'hb-builder/constants/imageArrangement';

export const InfiniteSliderItemComponent = ({
  activeItem,
  index,
  numberOfItem,
  sliderItemAttributes,
  subdomainNetworkUrl,
  toggleDrawer,
  ...item
}: InfiniteSliderItemComponentProps<ComputedSliderItems>) => {
  const { attributes, id } = item;
  const {
    _imageData,
    backgroundPosition,
    caption,
    galleryImage,
    imageAlt,
    url,
    isLoadingSrc,
  } = attributes || {};
  const { fontFamily, textColor } = sliderItemAttributes || {};

  const image = _imageData || galleryImage || url;

  const fullImageUrl =
    image?.startsWith('/images/missing') && subdomainNetworkUrl
      ? `${subdomainNetworkUrl}${image}`
      : image;

  const handleClickSliderItem = () => {
    toggleDrawer?.(item);
  };

  const isMiddleItem = index === activeItem;
  const isLeft = isLeftItem(index, activeItem, numberOfItem);
  const isRight = isRightItem(index, activeItem, numberOfItem);

  return (
    <Wrapper key={id}>
      {url && (
        <>
          <ImageWrapper
            $activeItem={isMiddleItem}
            $leftItem={isLeft}
            $rightItem={isRight}
            onClick={handleClickSliderItem}
          >
            <StyledImage
              $bgPos={backgroundPosition || BACKGROUND_POSITION.CENTER}
              $image={fullImageUrl}
              $activeItem={isMiddleItem}
              $leftItem={isLeft}
              $rightItem={isRight}
            >
              {isLoadingSrc && (
                <LoadingBox>
                  <Loader size={40} />
                </LoadingBox>
              )}
              {imageAlt && <span role="img" aria-label={imageAlt} />}
            </StyledImage>
          </ImageWrapper>

          <StyledText
            $hasCaption={isMiddleItem && !!caption}
            color={textColor}
            $fontFamily={fontFamily}
          >
            {isMiddleItem && caption}
          </StyledText>
        </>
      )}
    </Wrapper>
  );
};

const Wrapper = styled(Box).attrs(() => ({
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'inherit',
}))``;

interface StyledImagePropsExtended
  extends StyledImageProps,
    ImageWrapperProps {}

const StyledImage = styled(Box).attrs<StyledImagePropsExtended>(
  ({ $activeItem, $leftItem, $rightItem }) => ({
    mr: $rightItem ? 5 : undefined,
    ml: $leftItem ? 5 : undefined,
    height: { xl: 575, l: 400, m: 299, s: 217 },
    width: {
      xl: $activeItem ? 910 : 205,
      l: $activeItem ? 632 : 159,
      m: $activeItem ? 473 : 84,
      s: $activeItem ? 350 : undefined,
    },
    overflow: 'hidden',
  }),
)<StyledImagePropsExtended>`
  ${({ theme, $activeItem, $rightItem, $leftItem }) => css`
    border-radius: ${$activeItem && theme.themeVariables.globalBorderRadius}px;
    border-top-right-radius: ${$leftItem &&
    theme.themeVariables.globalBorderRadius}px;
    border-bottom-right-radius: ${$leftItem &&
    theme.themeVariables.globalBorderRadius}px;
    border-top-left-radius: ${$rightItem &&
    theme.themeVariables.globalBorderRadius}px;
    border-bottom-left-radius: ${$rightItem &&
    theme.themeVariables.globalBorderRadius}px;
  `}
  background-image: ${({ $image }) => ($image ? `url(${$image})` : null)};
  background-position: ${({ $bgPos }) => $bgPos};
  background-repeat: no-repeat;
  background-size: cover;
  opacity: ${({ $activeItem }) => !$activeItem && 0.3};
  position: relative;
  z-index: ${({ $activeItem }) => $activeItem && 1};
`;

interface ImageWrapperProps {
  $activeItem: boolean;
  $leftItem: boolean;
  $rightItem: boolean;
}

const ImageWrapper = styled(Box).attrs<ImageWrapperProps>(
  ({ $activeItem, $leftItem, $rightItem }) => {
    const getJustifyContent = () => {
      if ($leftItem) {
        return 'start';
      }
      if ($rightItem) {
        return 'end';
      }

      return 'center';
    };

    return {
      width: $activeItem ? undefined : '100%',
      height: '100%',
      overflow: 'hidden',
      position: 'relative',
      justifyContent: getJustifyContent(),
    };
  },
)<ImageWrapperProps>``;

interface StyledTextPropsExtended extends StyledTextProps {
  $hasCaption: boolean;
}

const StyledText = styled(Box).attrs<StyledTextPropsExtended>(
  ({ $hasCaption }) => ({
    mt: $hasCaption ? 2 : 4,
    mb: $hasCaption ? 2 : 4,
  }),
)<StyledTextPropsExtended>`
  font-family: ${({ $fontFamily }) => $fontFamily};
  word-break: break-word;
`;
